import { Clock } from '@saleshandy/icons';
import React, { memo, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router';
import NoResultGradientImage from '../../../../shared/components/images/no-result-gradient';
import SearchGrey from '../../../../shared/components/images/search-grey';
import Modal from '../../../../shared/design-system/components/atoms/modal';
import { redirectWithoutRefresh } from '../../../../shared/utils';
import { accessibleOnClick } from '../../../../shared/utils/accessible-on-click';
import { RootState } from '../../../../store/root-reducer';
import { capitalizeFirstLetter } from '../../helpers/helper';
import {
  setAiSearchPopupInputValue,
  setAiSearchPopupSubmitted,
  setIsAiSearchInProgress,
  setShowAiSearchPopup,
} from '../../lead-slice';
import {
  aiSuggestions,
  getAiSearchShortcut,
  getUserRecentSearches,
  userOsIdentifier,
} from '../ai-search-bar/helper';

interface AiSearchPopupProps {
  show: boolean;
  onClose?: () => void;
}

/**
 * AiSearchPopup Component
 *
 * Props:
 * - show (boolean): Controls the visibility of the popup. When `true`, the popup is displayed.
 * - onClose (optional callback): An optional function that will be called when the popup is closed.
 *
 * @returns {React.Element} JSX element representing the AI Search Popup.
 */

const AiSearchPopup: React.FC<AiSearchPopupProps> = memo(
  ({ show = false, onClose = () => {} }) => {
    const dispatch = useDispatch();
    const inputRef = useRef<HTMLInputElement | null>(null);
    const aiSearchInputValue = useSelector(
      (state: RootState) => state?.leads?.aiSearchInputValue,
    );
    const userEmail = useSelector((state: RootState) => state?.home?.email);

    const location = useLocation();

    const [shortcut, setShortcut] = useState<string>('');
    // const queryParams = new URLSearchParams(location?.search);
    const [searchTerm, setSearchTerm] = useState<string>(
      aiSearchInputValue || '',
    );
    const [popupRecentUrls, setPopupRecentUrls] = useState<any[]>([]);
    const [characterLimitError, setCharacterLimitError] = useState<string>('');

    // Handlers
    const handleAiSearchSubmit = useCallback(() => {
      // Create a fresh params object with only the required parameters
      const newParams = new URLSearchParams();
      newParams?.set('start', '1');
      newParams?.set('take', '25');

      // Redirect with the new paramss
      redirectWithoutRefresh(`?${newParams?.toString()}`);

      dispatch(setIsAiSearchInProgress(false));
      dispatch(setAiSearchPopupSubmitted(true));
      dispatch(setShowAiSearchPopup(false));
    }, [dispatch, location]);

    const handleAiSearchInputChange = useCallback(
      (e: React.ChangeEvent<HTMLInputElement>) => {
        // ** Keeping the copy of state in redux to reuse elsewhere
        setSearchTerm(e?.target?.value || '');
        dispatch(setAiSearchPopupInputValue(e?.target?.value || ''));
      },
      [dispatch],
    );

    const handleSuggestionClick = useCallback(
      (text: string) => {
        dispatch(setAiSearchPopupInputValue(text));
        setSearchTerm(text);
        inputRef?.current?.focus();
      },
      [dispatch],
    );

    const clearAiSearchInput = useCallback(() => {
      setSearchTerm('');
      dispatch(setAiSearchPopupInputValue(''));
    }, [dispatch]);

    const handleSearchTermValidationOnEnter = (e) => {
      if (e.keyCode === 13) {
        if (searchTerm?.length < 15) {
          setCharacterLimitError(
            'Please enter at least 15 characters to perform a search.',
          );
          return;
        }

        if (searchTerm?.length > 600) {
          setCharacterLimitError(
            'Your search term is too long. Please shorten it to 600 characters or less.',
          );
          return;
        }
        handleAiSearchSubmit();
      }
    };

    useEffect(() => {
      const platform = getAiSearchShortcut(userOsIdentifier());
      setShortcut(platform);
      if (show && inputRef?.current) {
        inputRef?.current?.focus();
      }
    }, []);

    useEffect(() => {
      setPopupRecentUrls(getUserRecentSearches(userEmail));
    }, []);

    return (
      <Modal
        show={show}
        hideHeader={true}
        onClose={onClose}
        onHide={onClose}
        className="ai-search-popup-container"
        hideFooter={true}
      >
        <div className="ai-search-popup-container">
          <div className="search-bar-suggestion-container d-flex">
            <div
              className="search-bar-header"
              {...accessibleOnClick(() => inputRef?.current?.focus())}
            >
              {/* Search Bar Input Container */}
              <div
                className={`search-bar-input-conatiner ${
                  characterLimitError ? 'error-border' : ''
                }${searchTerm?.length > 0 ? 'input-has-text' : ''}`}
              >
                <div className="search-bar-inner-content">
                  <span>
                    <SearchGrey />
                  </span>
                  <input
                    ref={inputRef}
                    type="text"
                    className="search-bar-input"
                    onChange={handleAiSearchInputChange}
                    onKeyDown={handleSearchTermValidationOnEnter}
                    tabIndex={0}
                    value={searchTerm}
                    placeholder="e.g. Find startups in California with fewer than 100 employees."
                  />
                  {searchTerm?.length > 0 ? (
                    <button
                      type="button"
                      {...accessibleOnClick(clearAiSearchInput)}
                      className="clear-icon"
                    >
                      <i className="sh-close-o" />
                    </button>
                  ) : (
                    <div className="search-bar-shortcut-key">
                      <NoResultGradientImage />
                      <div className="shortcut-key">
                        <span>{shortcut || 'Ctrl+K'}</span>
                      </div>
                    </div>
                  )}
                </div>
              </div>
              {/* Character Limit Error */}
              {characterLimitError && (
                <span className="character-limit-error">
                  {characterLimitError}
                </span>
              )}
            </div>

            {/* AI Suggestion Container */}
            <div
              className={`ai-suggestion-container d-flex ${
                popupRecentUrls?.length > 0 ? 'recent-searches' : ''
              }`}
            >
              <div className="search-ai-suggestion">
                {popupRecentUrls?.length > 0 ? (
                  <div className="search-ai-suggestion-recent d-flex">
                    <span className="ai-suggestion-header">
                      Recent Searches
                    </span>
                    {popupRecentUrls?.map((text, index) => (
                      <div
                        key={`recent-search ${index + 1}`}
                        className="ai-suggestion-recent"
                        {...accessibleOnClick(() => {
                          dispatch(
                            setAiSearchPopupInputValue(
                              text?.aiSearchInputValues,
                            ),
                          );
                          setSearchTerm(text?.aiSearchInputValues);
                          inputRef?.current?.focus();
                        })}
                      >
                        <div className="clock-bg d-flex">
                          <Clock className="clock-icon" />
                        </div>
                        <div className="ai-suggestion-recent-searches">
                          <span>
                            {capitalizeFirstLetter(text?.aiSearchInputValues) ||
                              ''}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <>
                    <span className="ai-suggestion-header">AI Suggestions</span>
                    <div className="search-ai-suggestion">
                      {aiSuggestions?.map((item, index) => (
                        <div
                          className="ai-suggestion-first"
                          key={`ai-suggestion${index + 1}`}
                          {...accessibleOnClick(() => {
                            handleSuggestionClick(item);
                          })}
                        >
                          <span>{item}</span>
                        </div>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </Modal>
    );
  },
);
export default AiSearchPopup;
